import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';

import { rtApiUsageActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { Col, Div, Icon, Row } from '@pinpoint/design-kit';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { ApiUsageCard } from 'src/components/realtime_api/usage';
import { ApiReturnTimeCard } from 'src/components/realtime_api/return';
import { ApiResponseProp, RtCsProp } from 'src/store/typings';

function RtApiUsage({
  client,
  history,
  tenant,
  match: {
    params: { configId },
  },
  rtApiUsage: {
    config,
    configList,
    filter,
    usageEntries,
    usageMasks,
    rttEntries,
    rttSecondaryEntries,
    rttToDateEntries,
    rttHist,
    rttStats,
    generateUsageStatsStatus,
    fetchUsageEntriesStatus,
    fetchRttEntriesStatus,
    ytdTotalCount,
    mtdTotalCount,
    tdyTotalCount,
    ytdMatchedCount,
    mtdMatchedCount,
    tdyMatchedCount,
  },
}) {
  const [showUsage, setShowUsage] = useState(true);
  const dispatch = useDispatch();
  const fetchConfig = useCallback((cfgId) => dispatch(rtApiUsageActions.fetchConfig(cfgId)), [dispatch]);
  const fetchConfigList = useCallback((query) => dispatch(rtApiUsageActions.fetchConfigList(query)), [dispatch]);
  const handleUnmount = useCallback(() => {
    dispatch(rtApiUsageActions.reset());
  }, [dispatch]);

  const toggleUsageResponseTime = useCallback(() => setShowUsage(!showUsage), [showUsage]);
  const handleSelectUsageFilter = useCallback(
    (idx) => {
      dispatch(rtApiUsageActions.updateFilter(CONSTANTS.REAL_TIME_API.USAGE.FILTERS[idx]));
    },
    [dispatch]
  );

  const loadRtApiUsage = useCallback(() => {
    if (configId) fetchConfig(configId);
    else if (!configList?.length)
      fetchConfigList({
        filters: [
          {
            name: 'recent_status',
            op: 'in',
            val: [
              CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_SUCCESS,
              CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_GENERATION_FAILURE,
              CONSTANTS.REAL_TIME_API.CS_STATE.CONFIG_WARM_UP_FAILURE,
            ],
          },
          {
            name: 'score_type',
            op: 'not_in',
            val: ['static'],
          },
        ],
        order_by: [{ field: 'created_at', direction: 'asc' }],
      });
  }, [configId, fetchConfig, configList, fetchConfigList]);

  useEffect(() => {
    if (tenant) loadRtApiUsage();
  }, [tenant, loadRtApiUsage]);

  useEffect(() => handleUnmount, [handleUnmount]);

  return (
    <Div d="flex" flexDir="column" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem' }}>
        <Col justify="left" align="center" gutterWidth="0" d="flex" size="12">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push('/rt');
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
        </Col>
      </Row>
      <Row m={{ x: '1.5rem', t: '1.5rem' }} flexDir="row" flexGrow="1">
        <Col size="12" justify="left" gutterWidth="0" d="flex" alignSelf="stretch">
          {showUsage && (
            <ApiUsageCard
              client={client}
              config={config}
              configId={configId}
              entries={usageEntries}
              masks={usageMasks}
              generateStatsStatus={generateUsageStatsStatus}
              usageReady={fetchUsageEntriesStatus}
              usageFilter={filter}
              mtdMatchedCount={mtdMatchedCount}
              mtdTotalCount={mtdTotalCount}
              tdyMatchedCount={tdyMatchedCount}
              tdyTotalCount={tdyTotalCount}
              ytdMatchedCount={ytdMatchedCount}
              ytdTotalCount={ytdTotalCount}
              selectUsageFilter={handleSelectUsageFilter}
              toggleView={toggleUsageResponseTime}
              loadRtApiUsage={loadRtApiUsage}
            />
          )}
          {!showUsage && (
            <ApiReturnTimeCard
              client={client}
              config={config}
              configId={configId}
              generateStatsStatus={generateUsageStatsStatus}
              hist={rttHist}
              stats={rttStats}
              rttEntries={rttEntries}
              rttSecondaryEntries={rttSecondaryEntries}
              rttToDateEntries={rttToDateEntries}
              rttReady={fetchRttEntriesStatus}
              usageFilter={filter}
              selectUsageFilter={handleSelectUsageFilter}
              toggleView={toggleUsageResponseTime}
              loadRtApiUsage={loadRtApiUsage}
            />
          )}
        </Col>
      </Row>
    </Div>
  );
}

const mapStateToProps = ({ app, rtApiUsage }) => ({ client: app.client, tenant: app?.profile?.tenant, rtApiUsage });

RtApiUsage.propTypes = {
  client: PropTypes.shape({
    billingDay: PropTypes.number.isRequired,
    enableRealTimeAPICaching: PropTypes.bool.isRequired,
  }).isRequired,
  tenant: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({ params: PropTypes.shape({ configId: PropTypes.string }) }).isRequired,
  rtApiUsage: PropTypes.shape({
    fetchUsageEntriesStatus: ApiResponseProp,
    fetchConfigUnifiedStatus: ApiResponseProp,
    fetchRttEntriesStatus: ApiResponseProp,
    generateUsageStatsStatus: ApiResponseProp,
    generateRttStatsStatus: ApiResponseProp,
    config: RtCsProp,
    filter: PropTypes.oneOf(CONSTANTS.REAL_TIME_API.USAGE.FILTERS),
    configList: PropTypes.arrayOf(RtCsProp).isRequired,
    usageEntries: PropTypes.arrayOf(
      PropTypes.shape({
        period: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        meta: PropTypes.string,
      })
    ),
    usageMasks: PropTypes.shape({
      ytdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      mtdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      mtdTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      tdyMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      tdyTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
      btdUnmatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    }).isRequired,
    rttEntries: PropTypes.arrayOf(
      PropTypes.shape({
        period: PropTypes.string,
        count: PropTypes.number,
        meta: PropTypes.string,
      })
    ),
    rttSecondaryEntries: PropTypes.arrayOf(PropTypes.shape({})),
    rttToDateEntries: PropTypes.arrayOf(PropTypes.shape({})),
    rttHist: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired,
        left: PropTypes.number.isRequired,
        right: PropTypes.number.isRequired,
      })
    ).isRequired,
    rttStats: PropTypes.shape({
      mean: PropTypes.number.isRequired,
      p50: PropTypes.number.isRequired,
      p75: PropTypes.number.isRequired,
      p90: PropTypes.number.isRequired,
    }).isRequired,
    ytdTotalCount: PropTypes.number,
    mtdTotalCount: PropTypes.number,
    tdyTotalCount: PropTypes.number,
    ytdMatchedCount: PropTypes.number,
    mtdMatchedCount: PropTypes.number,
    tdyMatchedCount: PropTypes.number,
  }).isRequired,
};

export default connect(mapStateToProps)(Page(RtApiUsage));
